/**
 * Custom hook for handling ticket scanning logic
 * Manages scan state, API calls, and feedback (audio + toast)
 */

import { useCallback, useState } from 'react';
import { ScanApiError, scanTicket } from '../api/scan-tool.api';
import { errorToast, successToast } from '../components/toast';
import lang from '../i18n/lang';
import { playErrorSound, playSuccessSound } from '../utils/audio-feedback';
import { ModuleTypeEnum } from '../dtos/participantdata/scanmodule/module-type.enum';

export type ScanStatus = 'idle' | 'success' | 'error';

export interface ScanResult {
  status: ScanStatus;
  message: string;
  isScanning: boolean;
}

interface UseScanHandlerOptions {
  scanModuleId: string;
  token: string;
  moduleType: ModuleTypeEnum;
  action?: string;
  onSuccess?: () => void;
  onError?: () => void;
}

export const useScanHandler = ({
  scanModuleId,
  token,
  moduleType,
  action,
  onSuccess,
  onError,
}: UseScanHandlerOptions) => {
  const [delegateNumber, setDelegateNumber] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState<ScanStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [scanCooldown, setScanCooldown] = useState(false);

  /**
   * Main scan handler - processes a delegate number through the API
   * Handles success, error, and special cases (limit reached, inactive nomination)
   */
  const handleScan = useCallback(
    async (dn: string, action?: string) => {
      if (isScanning || !scanModuleId || scanCooldown) return;

      // Set cooldown immediately to prevent duplicate scans
      setScanCooldown(true);
      setIsScanning(true);
      setDelegateNumber(dn);

      try {
        const result = await scanTicket(
          scanModuleId,
          dn,
          token,
          action,
        );

        if (result) {
          const message = result.scanMessage;

          setSuccessMessage(message);
          setScanStatus('success');
          playSuccessSound();

          // Cooldown already set at start of handleScan

          // Auto-close success overlay after 1.5 seconds
          setTimeout(() => {
            setScanStatus('idle');
            setSuccessMessage('');
            successToast(message);
            setDelegateNumber('');
            setIsScanning(false);
            onSuccess?.();
          }, 1500);

          // Reset cooldown after 5 seconds to prevent duplicate scans
          setTimeout(() => {
            setScanCooldown(false);
          }, 5000);
        }
      } catch (error: unknown) {
        // Error case - use API message if available, otherwise use fallback
        // Include delegateNumber in message if available
        const baseMessage =
          error instanceof ScanApiError ? error.apiMessage : lang('scan.error');
        const message = dn ? `${dn}: ${baseMessage}` : baseMessage;

        setErrorMessage(message);
        setScanStatus('error');
        playErrorSound();
        setDelegateNumber('');
        setIsScanning(false);
        onError?.();
      }
    },
    [isScanning, scanModuleId, token, onSuccess, onError, scanCooldown],
  );

  /**
   * Resets the scan state after an error
   * Allows the user to try scanning again
   * Shows error toast before resetting
   * Sets cooldown to prevent multiple error scans
   */
  const resetError = useCallback(() => {
    // Show error toast before resetting if there's an error message
    if (errorMessage) {
      errorToast(errorMessage);
    }
    setScanStatus('idle');
    setErrorMessage('');
    setDelegateNumber('');

    // Set cooldown to prevent multiple error scans (5 seconds)
    setScanCooldown(true);
    setTimeout(() => {
      setScanCooldown(false);
    }, 5000);
  }, [errorMessage]);

  return {
    // State
    delegateNumber,
    isScanning,
    scanStatus,
    errorMessage,
    successMessage,
    scanCooldown,

    // Actions
    setDelegateNumber,
    handleScan,
    resetError,
  };
};
